import type { AppOnlyFeatureKey } from '@/shared/constants/appOnlyFeatures';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** M-06 처방 등록 모달을 여는 데 필요한 거래처 정보 (M-04 [등록하기] · M-16 [+ 이미지 등록]) */
export interface PrescriptionUploadTarget {
  /** 업로드 API 경로 키 — URL 발급과 완료가 같은 거래처여야 한다 */
  relationshipId: number;
  clinicName: string;
  /** 거래처 대표 상태 statusView — 등록 가능 여부 판정용 */
  statusView: string;
  /** 실적 화면에서 연 경우 보던 정산월 — 등록 결과 정산월이 다르면 완료 스낵바로 알린다 */
  viewingSettlementYearMonth?: string;
}

/** M-15 C — 실적 화면에서 연 의약품 드로어에 함께 보여 줄 "그 달 그 병의원" 처방량 */
export interface DrugOpenContext {
  drugCode: string;
  /** 예: "2026.07 · 브이아이씨365소아청소년과의원" */
  label: string;
  quantity: number | null;
}

interface UIState {
  drugContext: DrugOpenContext | null;
  /** 열려 있는 처방 등록 대상. null이면 닫힘 */
  prescriptionUpload: PrescriptionUploadTarget | null;
  /** X6 공통 앱 안내 팝업에 띄울 기능. null이면 닫힘 */
  appOnlyFeature: AppOnlyFeatureKey | null;
  /** M-04 상단 1차 범위 배너를 닫았는지 (브라우저에만 저장) */
  scopeBannerDismissed: boolean;
  /**
   * M-15 B 2단 스택 — 거래 정보 드로어(M-20)가 열려 있는 동안 true.
   * M-12 드로어가 그 위에 24px 왼쪽으로 비켜 겹친다.
   */
  drugDrawerStacked: boolean;
}

interface UIActions {
  setDrugContext: (ctx: DrugOpenContext | null) => void;
  openPrescriptionUpload: (target: PrescriptionUploadTarget) => void;
  closePrescriptionUpload: () => void;
  openAppOnly: (feature: AppOnlyFeatureKey) => void;
  closeAppOnly: () => void;
  dismissScopeBanner: () => void;
  setDrugDrawerStacked: (stacked: boolean) => void;
}

export const useUIStore = create<UIState & UIActions>()(
  persist(
    (set) => ({
      prescriptionUpload: null,
      openPrescriptionUpload: (target) => set({ prescriptionUpload: target }),
      closePrescriptionUpload: () => set({ prescriptionUpload: null }),
      appOnlyFeature: null,
      scopeBannerDismissed: false,
      openAppOnly: (feature) => set({ appOnlyFeature: feature }),
      closeAppOnly: () => set({ appOnlyFeature: null }),
      dismissScopeBanner: () => set({ scopeBannerDismissed: true }),
      drugContext: null,
      setDrugContext: (ctx) => set({ drugContext: ctx }),
      drugDrawerStacked: false,
      setDrugDrawerStacked: (stacked) => set({ drugDrawerStacked: stacked }),
    }),
    {
      name: 'rudalink-web-ui',
      partialize: (state) => ({
        scopeBannerDismissed: state.scopeBannerDismissed,
      }),
    },
  ),
);
